import {
  Page, Card, Text, Badge, Banner, Button, Box, InlineStack, BlockStack,
  IndexTable, Spinner,
} from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { getTireJob, applyTireJob, type TireDisposition } from '../api/tires';

export function TireJobDetail() {
  const params = useParams();
  const id = parseInt(params.id ?? '', 10);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['tire-job', id],
    queryFn: () => getTireJob(id),
    enabled: Number.isFinite(id) && id > 0,
    refetchInterval: (query) => {
      const job = (query.state.data as any)?.job;
      return job?.status === 'pending' || job?.status === 'processing' ? 3000 : false;
    },
  });

  const applyMut = useMutation({
    mutationFn: () => applyTireJob(id),
    onSettled: () => {
      // Refetch on success AND error — backend may have flipped the job
      // status even on a 4xx response (e.g. idempotent re-apply), and the UI
      // must mirror DB state, never linger on stale "preview" badges.
      qc.invalidateQueries({ queryKey: ['tire-job', id] });
      qc.invalidateQueries({ queryKey: ['tire-jobs'] });
      qc.invalidateQueries({ queryKey: ['tires'] });
      qc.invalidateQueries({ queryKey: ['tire-stats'] });
    },
    onSuccess: (data) => {
      console.info('[tire-job] apply succeeded, jobId:', id, data);
    },
    onError: (err) => {
      console.error('[tire-job] apply failed, jobId:', id, err);
    },
  });

  if (!Number.isFinite(id)) return <Page title="Not found" />;
  if (isLoading) return <Page title="Tire job"><Spinner /></Page>;
  if (error) {
    return (
      <Page title="Tire job">
        <Banner tone="critical">Failed to load: {(error as Error).message}</Banner>
      </Page>
    );
  }

  const job = data?.job;
  const dispositions = data?.dispositions ?? [];
  const samples = data?.samples ?? ({} as Record<TireDisposition, unknown[]>);
  if (!job) return <Page title="Tire job"><Banner>Not found</Banner></Page>;

  const canApply = job.status === 'preview' && (job.valid_count ?? 0) > 0;
  const isFinal = job.status === 'applied' || job.status === 'failed';

  return (
    <Page
      title={`Tire job ${job.id}`}
      subtitle={`${job.vendor} · ${job.source_kind} · created ${new Date(job.created_at).toLocaleString()}`}
      backAction={{ content: 'Tire jobs', onAction: () => navigate('/tires') }}
      primaryAction={
        canApply
          ? {
              content: applyMut.isPending ? 'Applying…' : 'Apply (write tire_products)',
              onAction: () => {
                console.info('[tire-job] apply clicked, jobId:', id);
                if (applyMut.isPending) {
                  console.warn('[tire-job] apply ignored — request already in flight');
                  return;
                }
                applyMut.mutate();
              },
              loading: applyMut.isPending,
              disabled: applyMut.isPending,
            }
          : undefined
      }
    >
      <BlockStack gap="400">
        {applyMut.error && (
          <Banner tone="critical">Apply failed: {(applyMut.error as Error).message}</Banner>
        )}
        {applyMut.data && (
          <Banner tone="success">
            Applied: {applyMut.data.inserted} inserted, {applyMut.data.updated} updated,{' '}
            {applyMut.data.skipped} skipped
          </Banner>
        )}
        {job.error && (
          <Banner tone="critical">Job error: {job.error}</Banner>
        )}

        <Card>
          <BlockStack gap="200">
            <InlineStack gap="200">
              <Badge tone={statusTone(job.status)}>{job.status}</Badge>
              {job.dry_run && <Badge>dry-run</Badge>}
              {!isFinal && (job.status === 'pending' || job.status === 'processing') && (
                <Text as="span" variant="bodySm" tone="subdued">refreshing every 3s…</Text>
              )}
            </InlineStack>

            <Box paddingBlockStart="200">
              <Text as="p" variant="bodyMd">
                <strong>Brands:</strong> {(job.brands ?? []).join(', ') || '(default list)'} ·{' '}
                <strong>ProductGroups:</strong> {(job.product_groups ?? []).join(', ') || '(all 6 tire groups)'}
              </Text>
            </Box>

            <Box paddingBlockStart="200">
              <InlineStack gap="600">
                <Stat label="Raw rows"        value={job.raw_row_count} />
                <Stat label="Valid"           value={job.valid_count} />
                <Stat label="Skipped"         value={job.skipped_count} />
                <Stat label="Toyo blocked"    value={job.toyo_skipped_count} />
                <Stat label="Needs review"    value={job.needs_review_count} />
                <Stat label="Inserted"        value={job.inserted_count} />
                <Stat label="Updated"         value={job.updated_count} />
              </InlineStack>
            </Box>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="200">
            <Text as="h3" variant="headingMd">Dispositions</Text>
            {dispositions.length === 0 ? (
              <Text as="p" variant="bodyMd" tone="subdued">
                {job.status === 'pending' || job.status === 'processing'
                  ? 'Processing — counts will appear when staging completes.'
                  : 'No staged rows yet.'}
              </Text>
            ) : (
              <InlineStack gap="400">
                {dispositions.map((d) => (
                  <Stat key={d.disposition} label={d.disposition} value={d.n} />
                ))}
              </InlineStack>
            )}
          </BlockStack>
        </Card>

        {(['new', 'update', 'duplicate', 'invalid', 'toyo_skipped'] as TireDisposition[]).map((dKey) => {
          const rows = samples[dKey] ?? [];
          if (rows.length === 0) return null;
          const headings: [{ title: string }, ...{ title: string }[]] = [
            { title: 'Row' },
            { title: 'SKU' },
            { title: 'Brand' },
            { title: 'Model' },
            { title: 'Size' },
            { title: 'Price' },
            { title: 'Install' },
            { title: 'Notes' },
          ];
          return (
            <Card key={dKey}>
              <Box padding="400">
                <Text as="h3" variant="headingMd">
                  Sample: {dKey} ({rows.length})
                </Text>
              </Box>
              <IndexTable
                resourceName={{ singular: 'row', plural: 'rows' }}
                itemCount={rows.length}
                selectable={false}
                headings={headings}
              >
                {rows.map((r: any, i: number) => {
                  const p = r.parsed_payload ?? {};
                  const issues = [
                    ...(r.errors ?? []),
                    ...(p.needs_review_reasons ?? []),
                  ].slice(0, 3).join(', ');
                  return (
                    <IndexTable.Row id={String(r.row_index)} key={r.row_index} position={i}>
                      <IndexTable.Cell>{r.row_index}</IndexTable.Cell>
                      <IndexTable.Cell>{p.vendor_sku ?? '—'}</IndexTable.Cell>
                      <IndexTable.Cell>{p.brand ?? '—'}</IndexTable.Cell>
                      <IndexTable.Cell>{p.model ?? '—'}</IndexTable.Cell>
                      <IndexTable.Cell>{p.tire_size_full ?? '—'}</IndexTable.Cell>
                      <IndexTable.Cell>
                        {p.price_cents != null ? `$${(Number(p.price_cents) / 100).toFixed(2)}` : '—'}
                      </IndexTable.Cell>
                      <IndexTable.Cell>{p.install_speed ?? '—'}</IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text as="span" variant="bodySm" tone="subdued">{issues || '—'}</Text>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  );
                })}
              </IndexTable>
            </Card>
          );
        })}
      </BlockStack>
    </Page>
  );
}

function statusTone(s: string): 'success' | 'info' | 'critical' | 'attention' | 'new' {
  if (s === 'applied') return 'success';
  if (s === 'preview') return 'info';
  if (s === 'failed') return 'critical';
  if (s === 'processing' || s === 'pending') return 'attention';
  return 'new';
}

function Stat({ label, value }: { label: string; value: number | string | null | undefined }) {
  return (
    <BlockStack gap="100">
      <Text as="span" variant="bodySm" tone="subdued">{label}</Text>
      <Text as="span" variant="headingMd">{value ?? 0}</Text>
    </BlockStack>
  );
}
